import { useCallback, useSyncExternalStore } from "react";
import { decodeScope, encodeScope } from "@/lib/scope";
import type { ScopeRef } from "./types";

const CHANGE_EVENT = "scope-picker:changed";

function subscribe(callback: () => void): () => void {
	const onStorage = () => callback();
	const onCustom = () => callback();
	window.addEventListener("storage", onStorage);
	window.addEventListener(CHANGE_EVENT, onCustom);
	return () => {
		window.removeEventListener("storage", onStorage);
		window.removeEventListener(CHANGE_EVENT, onCustom);
	};
}

function readSnapshot(key: string): string | null {
	try {
		return window.localStorage.getItem(key);
	} catch {
		return null;
	}
}

/**
 * Trzeci element krotki (`hasStored`) mowi, czy w localStorage LEZY juz wybor
 * uzytkownika — `false` znaczy "nikt jeszcze nic nie wybral", co odrozniamy od
 * wartosci rownej `fallback`. Konsumenci z `[value, setValue]` dzialaja bez zmian.
 */
export function useScopePersistedState(
	key: string,
	fallback: ScopeRef,
): [ScopeRef, (s: ScopeRef) => void, boolean] {
	const raw = useSyncExternalStore(
		subscribe,
		() => readSnapshot(key),
		() => null,
	);
	const value = (raw ? decodeScope(raw) : null) ?? fallback;

	const setValue = useCallback(
		(next: ScopeRef) => {
			try {
				window.localStorage.setItem(key, encodeScope(next));
			} catch {}
			window.dispatchEvent(new CustomEvent(CHANGE_EVENT));
		},
		[key],
	);

	return [value, setValue, raw !== null];
}
